import { memo as _$memo } from "@opentui/solid";
import { effect as _$effect } from "@opentui/solid";
import { createTextNode as _$createTextNode } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { createMemo, createSignal, onMount, Show } from "solid-js";
import { useSync } from "@tui/context/sync";
import { map, pipe, sortBy } from "remeda";
import { DialogSelect } from "@tui/ui/dialog-select";
import { useDialog } from "@tui/ui/dialog";
import { useSDK } from "../context/sdk";
import { DialogPrompt } from "../ui/dialog-prompt";
import { Link } from "../ui/link";
import { useTheme } from "../context/theme";
import { TextAttributes } from "@opentui/core";
import { DialogModel } from "./dialog-model";
import { useKeyboard } from "@opentui/solid";
import { Clipboard } from "@tui/util/clipboard";
import { useToast } from "../ui/toast";
const PROVIDER_PRIORITY = {
  opencode: 0,
  "opencode-go": 1,
  openai: 2,
  "github-copilot": 3,
  anthropic: 4,
  google: 5
};
export function createDialogProviderOptions() {
  const sync = useSync();
  const dialog = useDialog();
  const sdk = useSDK();
  const options = createMemo(() => {
    return pipe(sync.data.provider_next.all, sortBy(x => PROVIDER_PRIORITY[x.id] ?? 99), map(provider => ({
      title: provider.name,
      value: provider.id,
      description: {
        opencode: "(Recommended)",
        anthropic: "(Claude Max or API key)",
        openai: "(ChatGPT Plus/Pro or API key)",
        "opencode-go": "Low cost subscription for everyone"
      }[provider.id],
      category: provider.id in PROVIDER_PRIORITY ? "Popular" : "Other",
      async onSelect() {
        const methods = sync.data.provider_auth[provider.id] ?? [{
          type: "api",
          label: "API key"
        }];
        let index = 0;
        if (methods.length > 1) {
          index = await new Promise(resolve => {
            dialog.replace(() => _$createComponent(DialogSelect, {
              title: "Select auth method",
              get options() {
                return methods.map((x, index) => ({
                  title: x.label,
                  value: index
                }));
              },
              onSelect: option => resolve(option.value)
            }), () => resolve(null));
          });
        }
        if (index == null) return;
        const method = methods[index];
        if (method.type === "oauth") {
          const result = await sdk.client.provider.oauth.authorize({
            providerID: provider.id,
            method: index
          });
          if (result.data?.method === "code") {
            dialog.replace(() => _$createComponent(CodeMethod, {
              get providerID() {
                return provider.id;
              },
              get title() {
                return method.label;
              },
              index: index,
              get authorization() {
                return result.data;
              }
            }));
          }
          if (result.data?.method === "auto") {
            dialog.replace(() => _$createComponent(AutoMethod, {
              get providerID() {
                return provider.id;
              },
              get title() {
                return method.label;
              },
              index: index,
              get authorization() {
                return result.data;
              }
            }));
          }
        }
        if (method.type === "api") {
          return dialog.replace(() => _$createComponent(ApiMethod, {
            get providerID() {
              return provider.id;
            },
            get title() {
              return method.label;
            }
          }));
        }
      }
    })));
  });
  return options;
}
export function DialogProvider() {
  const options = createDialogProviderOptions();
  return _$createComponent(DialogSelect, {
    title: "Connect a provider",
    get options() {
      return options();
    }
  });
}
function AutoMethod(props) {
  const {
    theme
  } = useTheme();
  const sdk = useSDK();
  const dialog = useDialog();
  const sync = useSync();
  const toast = useToast();
  useKeyboard(evt => {
    if (evt.name === "c" && !evt.ctrl && !evt.meta) {
      const code = props.authorization.instructions.match(/[A-Z0-9]{4}-[A-Z0-9]{4,5}/)?.[0] ?? props.authorization.url;
      Clipboard.copy(code).then(() => toast.show({
        message: "Copied to clipboard",
        variant: "info"
      })).catch(toast.error);
    }
  });
  onMount(async () => {
    const result = await sdk.client.provider.oauth.callback({
      providerID: props.providerID,
      method: props.index
    });
    if (result.error) {
      dialog.clear();
      return;
    }
    await sdk.client.instance.dispose();
    await sync.bootstrap();
    dialog.replace(() => _$createComponent(DialogModel, {
      get providerID() {
        return props.providerID;
      }
    }));
  });
  return (() => {
    var _el$ = _$createElement("box"),
      _el$2 = _$createElement("box"),
      _el$3 = _$createElement("text"),
      _el$4 = _$createElement("text"),
      _el$6 = _$createElement("box"),
      _el$7 = _$createElement("text"),
      _el$8 = _$createElement("text"),
      _el$0 = _$createElement("text"),
      _el$1 = _$createTextNode(`c `),
      _el$10 = _$createElement("span");
    _$insertNode(_el$, _el$2);
    _$insertNode(_el$, _el$6);
    _$insertNode(_el$, _el$8);
    _$insertNode(_el$, _el$0);
    _$setProp(_el$, "paddingLeft", 2);
    _$setProp(_el$, "paddingRight", 2);
    _$setProp(_el$, "gap", 1);
    _$setProp(_el$, "paddingBottom", 1);
    _$insertNode(_el$2, _el$3);
    _$insertNode(_el$2, _el$4);
    _$setProp(_el$2, "flexDirection", "row");
    _$setProp(_el$2, "justifyContent", "space-between");
    _$insert(_el$3, () => props.title);
    _$insertNode(_el$4, _$createTextNode(`esc`));
    _$setProp(_el$4, "onMouseUp", () => dialog.clear());
    _$insertNode(_el$6, _el$7);
    _$setProp(_el$6, "gap", 1);
    _$insert(_el$6, _$createComponent(Link, {
      get href() {
        return props.authorization.url;
      },
      get fg() {
        return theme.primary;
      }
    }), _el$7);
    _$insert(_el$7, () => props.authorization.instructions);
    _$insertNode(_el$8, _$createTextNode(`Waiting for authorization...`));
    _$insertNode(_el$0, _el$1);
    _$insertNode(_el$0, _el$10);
    _$insertNode(_el$10, _$createTextNode(`copy`));
    _$effect(_p$ => {
      var _v$ = TextAttributes.BOLD,
        _v$2 = theme.text,
        _v$3 = theme.textMuted,
        _v$4 = theme.textMuted,
        _v$5 = theme.textMuted,
        _v$6 = theme.text,
        _v$7 = {
          fg: theme.textMuted
        };
      _v$ !== _p$.e && (_p$.e = _$setProp(_el$3, "attributes", _v$, _p$.e));
      _v$2 !== _p$.t && (_p$.t = _$setProp(_el$3, "fg", _v$2, _p$.t));
      _v$3 !== _p$.a && (_p$.a = _$setProp(_el$4, "fg", _v$3, _p$.a));
      _v$4 !== _p$.o && (_p$.o = _$setProp(_el$7, "fg", _v$4, _p$.o));
      _v$5 !== _p$.i && (_p$.i = _$setProp(_el$8, "fg", _v$5, _p$.i));
      _v$6 !== _p$.n && (_p$.n = _$setProp(_el$0, "fg", _v$6, _p$.n));
      _v$7 !== _p$.s && (_p$.s = _$setProp(_el$10, "style", _v$7, _p$.s));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined,
      s: undefined
    });
    return _el$;
  })();
}
function CodeMethod(props) {
  const {
    theme
  } = useTheme();
  const sdk = useSDK();
  const sync = useSync();
  const dialog = useDialog();
  const [error, setError] = createSignal(false);
  return _$createComponent(DialogPrompt, {
    get title() {
      return props.title;
    },
    placeholder: "Authorization code",
    onConfirm: async value => {
      const {
        error
      } = await sdk.client.provider.oauth.callback({
        providerID: props.providerID,
        method: props.index,
        code: value
      });
      if (!error) {
        await sdk.client.instance.dispose();
        await sync.bootstrap();
        dialog.replace(() => _$createComponent(DialogModel, {
          get providerID() {
            return props.providerID;
          }
        }));
        return;
      }
      setError(true);
    },
    description: () => (() => {
      var _el$12 = _$createElement("box"),
        _el$13 = _$createElement("text");
      _$insertNode(_el$12, _el$13);
      _$setProp(_el$12, "gap", 1);
      _$insert(_el$13, () => props.authorization.instructions);
      _$insert(_el$12, _$createComponent(Link, {
        get href() {
          return props.authorization.url;
        },
        get fg() {
          return theme.primary;
        }
      }), null);
      _$insert(_el$12, _$createComponent(Show, {
        get when() {
          return error();
        },
        get children() {
          var _el$14 = _$createElement("text");
          _$insertNode(_el$14, _$createTextNode(`Invalid code`));
          _$effect(_$p => _$setProp(_el$14, "fg", theme.error, _$p));
          return _el$14;
        }
      }), null);
      _$effect(_$p => _$setProp(_el$13, "fg", theme.textMuted, _$p));
      return _el$12;
    })()
  });
}
function ApiMethod(props) {
  const dialog = useDialog();
  const sdk = useSDK();
  const sync = useSync();
  const {
    theme
  } = useTheme();
  return _$createComponent(DialogPrompt, {
    get title() {
      return props.title;
    },
    placeholder: "API key",
    get description() {
      return {
        opencode: (() => {
          var _el$16 = _$createElement("box"),
            _el$17 = _$createElement("text"),
            _el$19 = _$createElement("text"),
            _el$20 = _$createTextNode(`Go to `),
            _el$21 = _$createElement("span"),
            _el$23 = _$createTextNode(` to get a key`);
          _$insertNode(_el$16, _el$17);
          _$insertNode(_el$16, _el$19);
          _$setProp(_el$16, "gap", 1);
          _$insertNode(_el$17, _$createTextNode(`OpenCode Zen gives you access to all the best coding models at the cheapest prices with a single API key.`));
          _$insertNode(_el$19, _el$20);
          _$insertNode(_el$19, _el$21);
          _$insertNode(_el$19, _el$23);
          _$insertNode(_el$21, _$createTextNode(`https://opencode.ai/zen`));
          _$effect(_p$ => {
            var _v$8 = theme.textMuted,
              _v$9 = theme.text,
              _v$0 = {
                fg: theme.primary
              };
            _v$8 !== _p$.e && (_p$.e = _$setProp(_el$17, "fg", _v$8, _p$.e));
            _v$9 !== _p$.t && (_p$.t = _$setProp(_el$19, "fg", _v$9, _p$.t));
            _v$0 !== _p$.a && (_p$.a = _$setProp(_el$21, "style", _v$0, _p$.a));
            return _p$;
          }, {
            e: undefined,
            t: undefined,
            a: undefined
          });
          return _el$16;
        })(),
        "opencode-go": (() => {
          var _el$24 = _$createElement("box"),
            _el$25 = _$createElement("text"),
            _el$27 = _$createElement("text"),
            _el$28 = _$createTextNode(`Go to `),
            _el$29 = _$createElement("span"),
            _el$31 = _$createTextNode(` and enable OpenCode Go`);
          _$insertNode(_el$24, _el$25);
          _$insertNode(_el$24, _el$27);
          _$setProp(_el$24, "gap", 1);
          _$insertNode(_el$25, _$createTextNode(`OpenCode Go is a $10 per month subscription that provides reliable access to popular open coding models with generous usage limits.`));
          _$insertNode(_el$27, _el$28);
          _$insertNode(_el$27, _el$29);
          _$insertNode(_el$27, _el$31);
          _$insertNode(_el$29, _$createTextNode(`https://opencode.ai/zen`));
          _$effect(_p$ => {
            var _v$1 = theme.textMuted,
              _v$10 = theme.text,
              _v$11 = {
                fg: theme.primary
              };
            _v$1 !== _p$.e && (_p$.e = _$setProp(_el$25, "fg", _v$1, _p$.e));
            _v$10 !== _p$.t && (_p$.t = _$setProp(_el$27, "fg", _v$10, _p$.t));
            _v$11 !== _p$.a && (_p$.a = _$setProp(_el$29, "style", _v$11, _p$.a));
            return _p$;
          }, {
            e: undefined,
            t: undefined,
            a: undefined
          });
          return _el$24;
        })()
      }[props.providerID] ?? undefined;
    },
    onConfirm: async value => {
      if (!value) return;
      await sdk.client.auth.set({
        providerID: props.providerID,
        auth: {
          type: "api",
          key: value
        }
      });
      await sdk.client.instance.dispose();
      await sync.bootstrap();
      dialog.replace(() => _$createComponent(DialogModel, {
        get providerID() {
          return props.providerID;
        }
      }));
    }
  });
}